import React, { useState, FormEvent } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useStore } from '../../contexts/StoreContext';
import { useThemeColor } from '../../hooks/useThemeColor';
import { isAppShell } from '../../marketing/utils/isInstalledApp';
import AuthGate from '../../components/AuthGate/AuthGate';
import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';

const Login: React.FC = () => {
  useThemeColor('#6B21A8');
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const { clearCurrentStore } = useStore();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [formErrors, setFormErrors] = useState({
    email: '',
    password: '',
  });
  const [enteringApp, setEnteringApp] = useState(false);

  if (isAuthenticated && !enteringApp) {
    return <Navigate to={isAppShell() ? '/stores' : '/'} replace />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    if (error) {
      clearError();
    }
  };

  const validateForm = (): boolean => {
    const errors = {
      email: '',
      password: '',
    };

    if (!formData.email) {
      errors.email = 'L’e-mail est obligatoire';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Format d’e-mail invalide';
    }

    if (!formData.password) {
      errors.password = 'Le mot de passe est obligatoire';
    }

    setFormErrors(errors);
    return !errors.email && !errors.password;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setEnteringApp(true);
      await login({
        ...formData,
        email: formData.email.trim(),
      });
      clearCurrentStore();
      navigate('/stores', { replace: true });
    } catch (err) {
      setEnteringApp(false);
      console.error('Login error:', err);
    }
  };

  return (
    <AuthGate
      mobileApp
      panel={{
        eyebrow: 'Connexion',
        title: <>De retour <em>dans le store.</em></>,
        lead: 'Confirmation, commandes, livraison.',
        points: ['File Centre', 'Envoi depuis la fiche', 'Règles dans le store'],
      }}
    >
      <p className="auth-kicker auth-kicker--desk">Connexion</p>
      <h2 className="auth-h auth-h--desk">Se connecter</h2>
      <p className="auth-sub auth-sub--desk">Votre espace ops COD.</p>
      <h2 className="auth-h auth-h--app">Connexion</h2>

      {error && (
        <div className="auth-alert auth-alert--error">{error}</div>
      )}

      <form className="auth-form" onSubmit={handleSubmit}>
        <Input
          label="Email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          error={formErrors.email}
          placeholder="vous@exemple.com"
          required
          fullWidth
          autoComplete="email"
        />

        <Input
          label="Mot de passe"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          error={formErrors.password}
          placeholder="Votre mot de passe"
          required
          fullWidth
          autoComplete="current-password"
        />

        <p className="auth-forgot auth-forgot--app">
          <Link to="/forgot-password">Mot de passe oublié</Link>
        </p>

        <div className="auth-form__dock">
          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            loading={isLoading}
          >
            Se connecter
          </Button>
        </div>
      </form>

      <div className="auth-foot auth-foot--pair">
        <p>
          <Link to="/register">Créer un compte</Link>
        </p>
        <p className="auth-foot__forgot">
          <Link to="/forgot-password">Mot de passe oublié</Link>
        </p>
      </div>
      <div className="auth-foot auth-foot--quiet">
        <p>
          <Link to="/super-admin/login">Portail super-admin</Link>
        </p>
      </div>
    </AuthGate>
  );
};

export default Login;
